import { EntityType } from '@protector/entities/powerbi-reports/constants';
import { useQuery } from '@tanstack/react-query';
import { getCurrentWorkspaceId, getSelectedCompany } from 'entities/company/company.selectors';
import { getConfigsReportsV2, getReportsV2 } from 'entities/powerbi-reports/powerbi-reports.service';
import { models } from 'powerbi-client';
import { useGetProperties } from 'querys/property/property.query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

const useGetReportsConfig = (entityId: string | undefined, entityType: EntityType) => {
  const { data, isLoading, isRefetching } = useQuery({
    queryKey: ['powerbi-reports-config', entityId, entityType],
    queryFn: () => getConfigsReportsV2(entityId!, entityType),
    enabled: !!entityId
  });
  return { data, isLoading, isRefetching };
};

const useGetReports = (entityId: string | undefined, entityType: EntityType, hasConfig: boolean) => {
  const { data, isLoading, isRefetching } = useQuery({
    queryKey: ['powerbi-reports', entityId, entityType],
    queryFn: () => getReportsV2(entityId!, entityType),
    enabled: !!entityId && hasConfig
  });
  return { data, isLoading, isRefetching };
};

export const useReportsData = () => {
  const selectedCompany = useSelector(getSelectedCompany);
  const { workspaceId: paramsWorkspaceId } = useParams();
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId);
  const workspaceId = paramsWorkspaceId ?? currentWorkspaceId ?? '';

  const entityType = useMemo(() => (!selectedCompany?.id ? EntityType.WORKSPACE : EntityType.ORGANIZATION), [selectedCompany]);
  const entityId = useMemo(
    () => (entityType === EntityType.WORKSPACE ? workspaceId : selectedCompany?.id),
    [workspaceId, selectedCompany, entityType]
  );

  const {
    data: configReportsData,
    isLoading: isLoadingConfigReports,
    isRefetching: isRefetchingConfigReports
  } = useGetReportsConfig(entityId, entityType);
  const hasConfig = !!configReportsData?.length;
  const {
    data: reportsData,
    isLoading: isLoadingReports,
    isRefetching: isRefetchingReports
  } = useGetReports(entityId, entityType, hasConfig);

  return useMemo(
    () => ({
      reportsLoaded: reportsData,
      isLoading:
        (!!entityId && (isLoadingConfigReports || isRefetchingConfigReports)) || (hasConfig && (isLoadingReports || isRefetchingReports)),
      entityId,
      entityType
    }),
    [reportsData, isLoadingConfigReports, isRefetchingConfigReports, hasConfig, isLoadingReports, isRefetchingReports, entityId, entityType]
  );
};

export const useReportFilters = () => {
  const selectedCompany = useSelector(getSelectedCompany);
  const { workspaceId: paramsWorkspaceId } = useParams();
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId);
  const workspaceId = paramsWorkspaceId ?? currentWorkspaceId ?? '';

  const { properties, isFetching } = useGetProperties(workspaceId, !!workspaceId);

  const createFilter = (table: string, column: string, values: string[]): models.IBasicFilter => {
    return {
      $schema: 'https://powerbi.com/product/schema#basic',
      target: {
        table,
        column
      },
      operator: 'In',
      displaySettings: {
        isHiddenInViewMode: true
      },
      values,
      filterType: models.FilterType.Basic
    };
  };

  const filters: models.ReportLevelFilters[] = useMemo(() => {
    const baseFilters = [createFilter('filtered_workspace', 'workspaceid', [workspaceId])];
    if (selectedCompany) {
      baseFilters.push(createFilter('filtered_organization', 'orgid', [selectedCompany?.id]));
    }
    if (properties) {
      const propertyIds = properties.length > 0 ? properties.map(property => property.id) : ['__NO_MATCH__'];

      baseFilters.push(createFilter('filtered_properties', 'propertyid', propertyIds));
    }

    return baseFilters;
  }, [workspaceId, selectedCompany, properties]);

  return {
    filters,
    isFetchingFilters: isFetching
  };
};

export const useHasWorkspacePowerBIReports = (workspaceId: string | null | undefined) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['powerbi-workspace-configs', workspaceId],
    queryFn: () => getConfigsReportsV2(workspaceId as string, EntityType.WORKSPACE),
    enabled: !!workspaceId
  });

  return useMemo(
    () => ({
      hasWorkspaceReports: !!data?.length,
      isLoadingWorkspaceReports: isLoading,
      hasWorkspaceReportsError: isError
    }),
    [data, isLoading, isError]
  );
};

export const useHasOrganizationPowerBIReports = (organizationId: string | null | undefined) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['powerbi-organization-configs', organizationId],
    queryFn: () => getConfigsReportsV2(organizationId as string, EntityType.ORGANIZATION),
    enabled: !!organizationId
  });

  return useMemo(
    () => ({
      hasOrganizationReports: !!data?.length,
      isLoadingOrganizationReports: isLoading,
      hasOrganizationReportsError: isError
    }),
    [data, isLoading, isError]
  );
};
